"use client";

import { useEffect, useState } from "react";
import { useTranslations } from "next-intl";
import { withBasePath } from "@/lib/paths";

/** Chrome's install event; not in the DOM lib because only Chromium fires it. */
interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
}

const DISMISSED_KEY = "murdoku:install-dismissed";

/**
 * "Put Murdoku on your home screen."
 *
 * Two worlds. Chromium fires `beforeinstallprompt`, which we hold on to and fire from our own
 * button — one tap, a real install.
 *
 * iOS has no such event and Apple gives pages no way to trigger "Add to Home Screen", so there is
 * no button we could render that would do anything. What iOS visitors get instead is the actual
 * two steps, with the share glyph DRAWN — "tap the share button" is not followable if you cannot
 * tell which button that is, and on an iPhone it moves between the top and the bottom of the
 * screen depending on the version.
 *
 * Registered here too: the service worker some platforms require before offering installation.
 */
export function InstallPrompt() {
  const t = useTranslations("pwa");
  const [installEvent, setInstallEvent] = useState<BeforeInstallPromptEvent | null>(null);
  const [showIos, setShowIos] = useState(false);
  const [dismissed, setDismissed] = useState(true);

  useEffect(() => {
    if ("serviceWorker" in navigator) {
      void navigator.serviceWorker.register(withBasePath("/sw.js"));
    }

    setDismissed(window.localStorage.getItem(DISMISSED_KEY) !== null);

    const standalone =
      window.matchMedia("(display-mode: standalone)").matches ||
      // iOS Safari's own flag, set when opened from a home-screen tile.
      ("standalone" in window.navigator && (window.navigator as { standalone?: boolean }).standalone === true);
    if (standalone) return;

    const onPrompt = (event: Event): void => {
      event.preventDefault();
      setInstallEvent(event as BeforeInstallPromptEvent);
    };
    window.addEventListener("beforeinstallprompt", onPrompt);

    const isIos = /iphone|ipad|ipod/.test(window.navigator.userAgent.toLowerCase());
    if (isIos) setShowIos(true);

    return () => window.removeEventListener("beforeinstallprompt", onPrompt);
  }, []);

  if (dismissed || (!installEvent && !showIos)) return null;

  const install = async (): Promise<void> => {
    if (!installEvent) return;
    await installEvent.prompt();
    const choice = await installEvent.userChoice;
    if (choice.outcome === "accepted") setInstallEvent(null);
  };

  const dismiss = (): void => {
    window.localStorage.setItem(DISMISSED_KEY, "1");
    setDismissed(true);
  };

  return (
    <aside data-testid="install-prompt" style={panel}>
      {installEvent ? (
        <p style={{ margin: 0, flex: "1 1 16rem" }}>
          <strong>{t("title")}</strong> {t("body")}
        </p>
      ) : (
        <div style={{ flex: "1 1 16rem" }}>
          <p style={{ margin: "0 0 0.5rem" }}>
            <strong>{t("title")}</strong> {t("iosLead")}
          </p>
          <ol data-testid="ios-steps" style={{ margin: 0, paddingLeft: "1.2rem", display: "grid", gap: "0.35rem" }}>
            <li>
              {t("iosStep1")}{" "}
              <ShareGlyph />
            </li>
            <li>{t("iosStep2")}</li>
          </ol>
        </div>
      )}
      {installEvent && (
        <button type="button" data-testid="install-now" onClick={install} style={primary}>
          {t("install")}
        </button>
      )}
      <button type="button" data-testid="install-dismiss" onClick={dismiss} style={secondary}>
        {t("notNow")}
      </button>
    </aside>
  );
}

/**
 * iOS's share mark: a box with an arrow leaving the top. Drawn rather than described, and inline
 * with the text at the size the system draws it, so it is recognisable at a glance.
 */
function ShareGlyph() {
  return (
    <svg
      width="1.05em"
      height="1.05em"
      viewBox="0 0 20 20"
      aria-hidden="true"
      style={{ verticalAlign: "-0.18em", margin: "0 0.1em" }}
    >
      <path
        d="M10 2.5v9M10 2.5 7.2 5.3M10 2.5l2.8 2.8"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M6 8.2H4.6v8.3h10.8V8.2H14"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

const panel = {
  display: "flex",
  flexWrap: "wrap",
  gap: "0.75rem",
  alignItems: "center",
  margin: "1.5rem 0 0",
  padding: "0.9rem 1rem",
  borderRadius: "0.6rem",
  border: "1px solid rgba(128,128,128,0.35)",
} as const;

const primary = {
  padding: "0.55rem 1rem",
  borderRadius: "0.45rem",
  background: "var(--accent)",
  border: "none",
  color: "#fff",
  fontWeight: 600,
  cursor: "pointer",
  minHeight: "2.75rem",
} as const;

const secondary = {
  padding: "0.55rem 1rem",
  borderRadius: "0.45rem",
  border: "1px solid rgba(128,128,128,0.5)",
  background: "transparent",
  color: "inherit",
  cursor: "pointer",
  minHeight: "2.75rem",
} as const;
